import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { ScopeProvider } from '@portal/pkg-authorization'
import GridView from '@migrate/components/navigator/GridView'
import { UserStorageQueries } from '@navigator/data/UserStorage'
import { UserStorageKey } from '@portal/pkg-api/gen/navigator'
import { useGridViewState } from '@shared/data/GridViewState'
import { EntityTypeQueries } from '@navigator/data/EntityType'
import { PerspectiveQueries } from '@navigator/data/Perspective'
import { isViewableEntityType } from '@navigator/entityTypeAccess'
import {
  buildCanonicalOpenTabs,
  perspectiveEntityTypeCode,
  resolveCanonicalEntityType,
} from '@routes/_auth/-grid.canonical'

function safeParseJSON<T>(str: string, fallback: T): T {
  try {
    const parsed = JSON.parse(str)
    return parsed ?? fallback
  } catch {
    return fallback
  }
}

export const Route = createFileRoute('/_auth/grid')({
  component: RouteComponent,
  validateSearch: z.object({
    hideentitypicker: z.boolean().optional(),
    hideviewpicker: z.boolean().optional(),
    // CRPM-3722 Coerce to string so numeric-looking codes (e.g. `?entityType=836`)
    // are kept as '836' instead of failing validation.
    entityType: z.coerce.string().optional(),
    p: z.number().optional(),
    utm_source: z.enum(['breadcrumb']).optional(),
    openTabs: z.string().optional(),
  }),
  loaderDeps: ({ search: { entityType, openTabs, p } }) => ({ entityType, openTabs, p }),
  // The URL is the single source of truth for which entity type is active and
  // which tabs are open. The loader's job is to canonicalize the URL before
  // the page renders: fill in the default entity type when missing, ensure
  // viewable pinned tabs and the active type are always in `openTabs`, and drop
  // codes the caller cannot view. If the URL doesn't already match the
  // canonical form, we replace it and let the loader run again with the new
  // (now-canonical) deps.
  //
  // When a perspective id (`p`) is in the URL, we resolve the perspective
  // server-side here and force `entityType` to match it — perspectives are
  // bound to a single entity type, so the URL would lie if it disagreed.
  //
  // This keeps GridView/GridViewTabs purely URL-driven — the components don't
  // need to reconcile internal state against props on every navigation.
  loader: async ({
    context: { queryClient, scopedEnablements },
    deps: { entityType, openTabs, p },
  }) => {
    const [stored, entityTypes] = await Promise.all([
      queryClient.ensureQueryData(UserStorageQueries.get(UserStorageKey.PINNED_TABS)),
      queryClient.ensureQueryData(EntityTypeQueries.entityTypes()),
    ])

    const parsed = safeParseJSON(stored.value, { pinnedTabs: [] })
    const pinnedTabs: string[] = Array.isArray(parsed.pinnedTabs) ? parsed.pinnedTabs : []
    // Pins for a type the caller cannot view stay in saved prefs. GridViewTabs
    // saves its whole pin list whenever the user changes a pin, so only the URL
    // and the open tabs use this filtered copy.
    const visiblePins = pinnedTabs.filter((code) =>
      entityTypes.some(
        (candidate) =>
          candidate.code === code && isViewableEntityType(candidate, scopedEnablements),
      ),
    )

    let canonicalEntityType = resolveCanonicalEntityType(
      entityType,
      entityTypes,
      visiblePins,
      scopedEnablements,
    )

    // A perspective is always bound to one entity type. If the URL has `p` and
    // the caller can view that type, it wins over whatever the URL said.
    // A perspective they cannot view is removed from the URL so the grid still
    // opens, with only the entity types they are allowed to see.
    let dropPerspective = false
    if (p !== undefined) {
      let perspectiveType: string | undefined
      try {
        const perspective = await queryClient.ensureQueryData({
          ...PerspectiveQueries.perspective(p),
          retry: false,
        })
        perspectiveType = perspectiveEntityTypeCode(perspective, entityTypes, scopedEnablements)
      } catch {
        // Fetch failure (404, 403, 500, network) — treat as inaccessible perspective.
      }
      if (!perspectiveType) {
        dropPerspective = true
      } else {
        canonicalEntityType = perspectiveType
      }
    }

    const canonicalOpenTabsArr = buildCanonicalOpenTabs(
      openTabs ?? '',
      canonicalEntityType,
      visiblePins,
      entityTypes,
      scopedEnablements,
    )
    const canonicalOpenTabs = canonicalOpenTabsArr.join('-') || undefined

    if (canonicalEntityType !== entityType || canonicalOpenTabs !== openTabs || dropPerspective) {
      throw redirect({
        from: '/grid',
        to: '/grid',
        search: (prev) => ({
          ...prev,
          entityType: canonicalEntityType,
          openTabs: canonicalOpenTabs,
          p: dropPerspective ? undefined : prev.p,
        }),
        replace: true,
      })
    }

    // The active tab is the scope; other open tabs are not.
    const activeEntityType = entityTypes.find((et) => et.code === canonicalEntityType)
    const scope = activeEntityType ? { entityTypeId: activeEntityType.id } : undefined

    return { pinnedTabs, openTabs: canonicalOpenTabsArr, scope }
  },
})

function RouteComponent() {
  const { entityType, hideentitypicker, hideviewpicker, p: perspectiveId } = Route.useSearch()
  const { pinnedTabs, openTabs, scope } = Route.useLoaderData()

  useGridViewState()

  return (
    <ScopeProvider scope={scope}>
      <GridView
        entityType={entityType}
        hidePicker={hideentitypicker}
        hideViewSelector={hideviewpicker}
        perspectiveId={perspectiveId}
        showViewsCollapsePanel
        openTabs={openTabs}
        pinnedTabs={pinnedTabs}
      />
    </ScopeProvider>
  )
}
